export function isValidUrl(string) {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
}

export function validateStep(step, context) {
  const {
    formData,
    startDate,
    endDate,
    redemptionLocationType,
    selectedBranches,
    redemptionLocationTypeStep4,
    selectAllRedemptionLocations,
    selectedRedemptionBranches,
  } = context;
    const errors = {};

    // eslint-disable-next-line default-case
    switch (step) {
      case 1:
        if (!formData.title.trim()) errors.title = "Title is required";
        if (!formData.product_name?.trim()) errors.product_name = "Product name is required";
        if (!formData.productCategory) errors.productCategory = "Product category is required";
        if (!formData.product_subcategory) errors.product_subcategory = "Product subcategory is required";

        // Offer type specific validation
        if (formData.offer_type === "discount") {
          if (
            !formData.discount_original_price ||
            !formData.discount_sale_price
          ) {
            errors.pricing =
              "Both original price and discounted price are required";
          }
        } else if (formData.offer_type === "bundle") {
          if (
            !formData.original_sum ||
            !formData.bundle_price ||
            !formData.bundle_savings
          ) {
            errors.bundle =
              "Original sum, bundle price, and savings are required";
          }
        } else if (formData.offer_type === "bogo") {
          if (
            !formData.bogo_item_price ||
            !formData.bogo_quantity ||
            !formData.bogo_free_quantity
          ) {
            errors.bogo = "Item price and BOGO quantities are required";
          }
        }

        // Validity validation
        if (formData.validity_type === "regular") {
          if (!startDate) errors.start_date = "Start date is required";
          if (!endDate) errors.expiry_date = "End date is required";
        } else if (formData.validity_type === "flash_sale") {
          if (!formData.flash_start_time)
            errors.flash_start_time = "Flash start time is required";
          if (!formData.flash_end_time)
            errors.flash_end_time = "Flash end time is required";
        }
        break;

      case 2:
        if (!formData.phone_number) {
          errors.phone_number = "Phone number is required";
        } else if (formData.phone_number.length !== 9) {
          errors.phone_number =
            "Phone number must be exactly 9 digits after 233";
        }

        // SMS content validation
        if (!formData.sms_content || formData.sms_content.trim().length < 10) {
          errors.sms_content =
            "SMS content must be at least 10 characters long";
        }

        // SKU validation
        if (formData.offer_type === "bundle") {
          if (!formData.bundle_skus || formData.bundle_skus.length === 0) {
            errors.bundle_skus =
              "At least one bundle item with SKU is required";
          } else {
            const hasEmptySkus = formData.bundle_skus.some(
              (item) => !item.product_name.trim() || !item.sku.trim()
            );
            if (hasEmptySkus) {
              errors.bundle_skus =
                "All bundle items must have both product name and SKU";
            }
          }
        } else {
          if (!formData.sku || !formData.sku.trim()) {
            errors.sku = "Product SKU is required";
          }
        }
        break;

      case 3:
        // Category validation
        if (formData.category.length === 0) {
          errors.category = "At least one category is required";
        }

        // Gender validation
        if (formData.gender.length === 0) {
          errors.gender = "Gender selection is required";
        }

        // Location validation
        if (redemptionLocationType === "region") {
          if (
            formData.region_location.length === 0 ||
            (formData.region_location.length === 1 &&
              !formData.region_location[0].region &&
              !formData.region_location[0].location)
          ) {
            errors.region_location =
              "Please select at least one region and location";
          }
        } else if (redemptionLocationType === "branch") {
          if (selectedBranches.length === 0) {
            errors.branch_location = "Please select at least one branch";
          }
        } else if (redemptionLocationType === "partner") {
          const validPartnerLocations = formData.partner_location.filter(
            (partner) => partner.branches && partner.branches.length > 0
          );
          if (validPartnerLocations.length === 0) {
            errors.partner_location =
              "Please select at least one partner branch";
          }
        }
        break;

      case 4:
        // Redemption type validation
        if (formData.redemption_type.length === 0) {
          errors.redemption_type = "At least one redemption type is required";
        }

        // Online link validation - required if Online redemption is selected
        if (formData.redemption_type.includes("Online")) {
          if (!formData.online_link || formData.online_link.trim() === "") {
            errors.online_link =
              "Online link is required when Online redemption is selected";
          } else if (!isValidUrl(formData.online_link)) {
            errors.online_link = "Please enter a valid URL";
          }
        }

        // In-store redemption location validation
        if (formData.redemption_type.includes("In-store")) {
          if (redemptionLocationTypeStep4 === "region") {
            if (
              !selectAllRedemptionLocations &&
              (!formData.redemption_region_location ||
                formData.redemption_region_location.length === 0 ||
                (formData.redemption_region_location.length === 1 &&
                  !formData.redemption_region_location[0].region &&
                  !formData.redemption_region_location[0].location))
            ) {
              errors.redemption_location =
                "Please select at least one redemption region and location";
            }
          } else if (redemptionLocationTypeStep4 === "branch") {
            if (selectedRedemptionBranches.length === 0) {
              errors.redemption_location =
                "Please select at least one redemption branch";
            }
          } else if (redemptionLocationTypeStep4 === "partner") {
            // Check if any partner has at least one branch selected
            const validPartnerLocations = formData.redemption_partner_location.filter(
              (partner) => partner.branches && partner.branches.length > 0
            );
            if (validPartnerLocations.length === 0) {
              errors.redemption_location =
                "Please select at least one branch from redemption partners";
            }
          }
        }

        if (
          !formData.terms_and_conditions ||
          formData.terms_and_conditions.trim().length < 10
        ) {
          errors.terms_and_conditions =
            "Terms & Conditions must be at least 10 characters long";
        }
        break;

      case 5:
        // Final validation - all previous steps should be valid
        const step1Errors = validateStep(1, context);
        const step2Errors = validateStep(2, context);
        const step3Errors = validateStep(3, context);
        const step4Errors = validateStep(4, context);

        if (Object.keys(step1Errors).length > 0) {
          errors.step1 = "Please complete Step 1 requirements";
        }
        if (Object.keys(step2Errors).length > 0) {
          errors.step2 = "Please complete Step 2 requirements";
        }
        if (Object.keys(step3Errors).length > 0) {
          errors.step3 = "Please complete Step 3 requirements";
        }
        if (Object.keys(step4Errors).length > 0) {
          errors.step4 = "Please complete Step 4 requirements";
        }
        break;
    }

    return errors;
}
